/**
 * ProviderRedirect - Route guard component for protected routes
 * 
 * Validates that user has an active Wialon session before
 * rendering protected content. Redirects to landing page if
 * session is not valid or has expired.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {
  selectIsAuthenticated,
  selectIsSessionActive,
  selectSid,
  selectBaseUrl,
} from '../reduxStore/WialonSessionReducer';
import Cookies from "universal-cookie";

const cookies = new Cookies();

function ProviderRedirect(props) {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);

  // Redux selectors - check session credentials AND active status
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isSessionActive = useSelector(selectIsSessionActive);
  const sid = useSelector(selectSid);
  const baseUrl = useSelector(selectBaseUrl);

  useEffect(() => {
    // Check both Redux state and cookies for session validity
    const cookieSid = cookies.get('sid');
    const cookieBaseUrl = cookies.get('baseUrl');
    const cookieActiveSession = cookies.get('active_session');

    // Check if session is marked as expired in Redux (isSessionActive = false)
    // This happens when WialonService detects an invalid session
    if (isAuthenticated && !isSessionActive) {
      console.log('[ProviderRedirect] Session expired, redirecting to sorry page...');
      // Clear the cookie so we don't loop
      cookies.set('active_session', 'no', { path: '/' });
      navigate('/sorry', { replace: true });
      return;
    }

    // Valid if either Redux state OR cookies indicate session credentials exist
    const hasValidReduxSession = isAuthenticated && sid && baseUrl && isSessionActive !== false;
    const hasValidCookieSession = cookieSid && cookieBaseUrl && cookieSid !== 'null' && cookieActiveSession !== 'no';

    if (!hasValidReduxSession && !hasValidCookieSession) {
      console.log('[ProviderRedirect] No valid session found, redirecting to landing page...');
      navigate('/');
      return;
    }

    // Session credentials exist and session is active
    setIsValidating(false);
  }, [isAuthenticated, isSessionActive, sid, baseUrl, navigate]);

  // Show loading while validating
  if (isValidating) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          position: 'relative',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Render protected content
  return (
    <Box sx={{ position: 'relative' }}>
      {props?.children}
    </Box>
  );
}

export default ProviderRedirect;

